import { useState, useRef } from "react";
import { useParams, Link } from "@tanstack/react-router";
import { ArrowLeft, Plus, Trash2, Edit2, Layers, Play, FileDown, AlertTriangle, CheckCircle, Unlock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  useCountingInvoice,
  useAddCountingItem,
  useUpdateCountingItem,
  useDeleteCountingItem,
  useAddCountingVariation,
  useBulkCountingVariations,
  useDeleteCountingVariation,
  useUpdateCountingVariation,
  useSetCountingStatus,
} from "@/hooks/use-counting";
import { toast } from "sonner";

function VariationRow({
  variation,
  invoiceId,
  vendorCode,
  onUpdate,
  onDelete,
  disabled,
}: {
  variation: any;
  invoiceId: string;
  vendorCode: string;
  onUpdate: (v: { varId: string; invoiceId: string; sku: string; expected_qty: number }) => Promise<any>;
  onDelete: (v: { varId: string; invoiceId: string }) => Promise<any>;
  disabled?: boolean;
}) {
  const prefix = vendorCode + "/";
  const initialSuffix = variation.sku.startsWith(prefix) ? variation.sku.slice(prefix.length) : variation.sku;
  const [suffix, setSuffix] = useState(initialSuffix);
  const [qty, setQty] = useState(String(variation.expectedQty));

  // Resync if server data changes (e.g. after refetch)
  const lastServerSuffix = useRef(initialSuffix);
  const lastServerQty = useRef(String(variation.expectedQty));
  if (lastServerSuffix.current !== initialSuffix) {
    lastServerSuffix.current = initialSuffix;
    setSuffix(initialSuffix);
  }
  if (lastServerQty.current !== String(variation.expectedQty)) {
    lastServerQty.current = String(variation.expectedQty);
    setQty(String(variation.expectedQty));
  }

  async function commit() {
    const cleanSuffix = suffix.trim().replace(/^\/+/, "");
    if (!cleanSuffix) return;
    const finalSku = `${prefix}${cleanSuffix}`;
    const newQty = Number(qty) || 0;
    if (finalSku === variation.sku && newQty === variation.expectedQty) return;
    await onUpdate({ varId: variation.id, invoiceId, sku: finalSku, expected_qty: newQty });
  }

  return (
    <div className="flex items-center gap-1.5 py-0.5">
      <div className="flex items-center flex-1 min-w-0 ring-1 ring-input bg-background h-7 focus-within:ring-2 focus-within:ring-ring">
        <span className="text-[11px] text-muted-foreground tabular-nums px-1.5 select-none whitespace-nowrap">{prefix}</span>
        <input
          value={suffix}
          onChange={(e) => setSuffix(e.target.value)}
          onBlur={commit}
          disabled={disabled}
          placeholder="boja/veličina"
          className="h-full text-[11px] tabular-nums flex-1 min-w-0 bg-transparent outline-none pr-1.5"
        />
      </div>
      <Input
        type="number"
        inputMode="numeric"
        value={qty}
        onChange={(e) => setQty(e.target.value)}
        onBlur={commit}
        disabled={disabled}
        className="h-7 text-[11px] tabular-nums text-right w-16 shrink-0"
      />
      {!disabled && (
        <button
          onClick={() => onDelete({ varId: variation.id, invoiceId })}
          className="text-muted-foreground/40 hover:text-destructive shrink-0 px-1"
          title="Obriši"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

function parseVariationColor(sku: string, vendorCode: string): string | null {
  const suffix = sku.startsWith(vendorCode + "/") ? sku.slice(vendorCode.length + 1) : null;
  if (!suffix) return null;
  const parts = suffix.split("/");
  return parts.length >= 2 ? parts[0]! : null;
}

export function CountingDetailPage() {
  const { invoiceId: rawInvoiceId } = useParams({ strict: false });
  const invoiceId = rawInvoiceId ?? "";
  const { data: invoice, isLoading } = useCountingInvoice(invoiceId || null);
  const addItem = useAddCountingItem();
  const updateItem = useUpdateCountingItem();
  const deleteItem = useDeleteCountingItem();
  const addVariation = useAddCountingVariation();
  const bulkVariations = useBulkCountingVariations();
  const deleteVariation = useDeleteCountingVariation();
  const updateVariation = useUpdateCountingVariation();
  const setStatus = useSetCountingStatus();

  const [showAddItem, setShowAddItem] = useState(false);
  const [itemForm, setItemForm] = useState({ vendorCode: "", name: "", quantity: "0" });
  const [editingItem, setEditingItem] = useState<any>(null);

  const [showGenerator, setShowGenerator] = useState<string | null>(null);
  const [genColors, setGenColors] = useState("");
  const [genSizes, setGenSizes] = useState("");
  const [genQuantities, setGenQuantities] = useState<Record<string, string>>({});

  const [showAddVar, setShowAddVar] = useState<string | null>(null);
  const [varForm, setVarForm] = useState({ sku: "", expectedQty: "0" });


  if (isLoading) {
    return (
      <div className="p-4 flex flex-col gap-4">
        <div className="h-8 w-48 bg-muted/30 animate-pulse" />
        <div className="h-32 bg-muted/30 animate-pulse" />
        <div className="h-32 bg-muted/30 animate-pulse" />
      </div>
    );
  }
  if (!invoice) return <div className="p-4 text-sm text-destructive">Faktura nije pronađena.</div>;

  const isDone = invoice.status === "DONE";
  const totalVariations = invoice.items.reduce((s: number, i: any) => s + i.variations.length, 0);
  const totalExpectedQty = invoice.items.reduce((s: number, i: any) =>
    s + i.variations.reduce((vs: number, v: any) => vs + v.expectedQty, 0), 0);

  async function handleAddItem() {
    await addItem.mutateAsync({
      invoiceId,
      vendor_code: itemForm.vendorCode,
      name: itemForm.name,
      quantity: Number(itemForm.quantity) || 0,
    });
    setShowAddItem(false);
    setItemForm({ vendorCode: "", name: "", quantity: "0" });
    toast.success("Artikal dodan");
  }

  async function handleUpdateItem() {
    if (!editingItem) return;
    await updateItem.mutateAsync({
      itemId: editingItem.id,
      invoiceId,
      vendor_code: editingItem.vendorCode,
      name: editingItem.name,
      quantity: Number(editingItem.quantity) || 0,
    });
    setEditingItem(null);
    toast.success("Ažurirano");
  }

  async function handleDeleteItem(itemId: string) {
    if (!confirm("Obrisati artikal i sve varijacije?")) return;
    await deleteItem.mutateAsync({ itemId, invoiceId });
    toast.success("Obrisano");
  }

  async function handleGenerate() {
    if (!showGenerator) return;
    const item = invoice.items.find((i: any) => i.id === showGenerator);
    if (!item) return;

    const colors = genColors.split(",").map((s: string) => s.trim()).filter(Boolean);
    const sizes = genSizes.split(",").map((s: string) => s.trim()).filter(Boolean);
    if (colors.length === 0 || sizes.length === 0) {
      toast.error("Unesite boje i veličine");
      return;
    }

    const variations = colors.flatMap((color: string) =>
      sizes.map((size: string) => {
        const sku = `${item.vendorCode}/${color}/${size}`;
        return {
          sku,
          expected_qty: Number(genQuantities[sku]) || 0,
        };
      })
    );

    await bulkVariations.mutateAsync({ itemId: showGenerator, invoiceId, variations });
    setShowGenerator(null);
    setGenColors("");
    setGenSizes("");
    setGenQuantities({});
    toast.success("Varijacije generirane");
  }

  async function handleAddVariation() {
    if (!showAddVar) return;
    const item = invoice.items.find((i: any) => i.id === showAddVar);
    if (!item) return;
    const raw = varForm.sku.trim().replace(/^\/+/, "");
    if (!raw) return;
    const fullSku = raw.startsWith(item.vendorCode + "/") || raw === item.vendorCode
      ? raw
      : `${item.vendorCode}/${raw}`;
    await addVariation.mutateAsync({
      itemId: showAddVar,
      invoiceId,
      sku: fullSku,
      expected_qty: Number(varForm.expectedQty) || 0,
    });
    setShowAddVar(null);
    setVarForm({ sku: "", expectedQty: "0" });
    toast.success("Varijacija dodana");
  }

  async function handleStartCounting() {
    if (invoice.status === "OPEN") {
      await setStatus.mutateAsync({ id: invoiceId, status: "COUNTING", expectedStatus: invoice.status });
    }
  }

  async function handleFinish() {
    if (!confirm("Završiti brojanje? Nakon toga se faktura zaključava.")) return;
    await setStatus.mutateAsync({ id: invoiceId, status: "DONE" });
    toast.success("Brojanje završeno");
  }

  async function handleReopen() {
    if (!confirm("Ponovo otvoriti fakturu za izmjene?")) return;
    await setStatus.mutateAsync({ id: invoiceId, status: "COUNTING", expectedStatus: invoice.status });
    toast.success("Faktura ponovo otvorena");
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 pb-3 border-b border-foreground/5">
        <div className="flex items-center gap-2.5 mb-3">
          <Link to="/warehouse/brojanje-robe">
            <Button variant="ghost" size="icon-xs"><ArrowLeft className="h-4 w-4" /></Button>
          </Link>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-semibold truncate tracking-tight">{invoice.name}</h1>
          </div>
          <Badge variant={invoice.status === "DONE" ? "secondary" : invoice.status === "COUNTING" ? "default" : "outline"}>
            {invoice.status === "DONE" ? "Završeno" : invoice.status === "COUNTING" ? "U toku" : "Otvoreno"}
          </Badge>
        </div>

        {/* Stats bar */}
        <div className="flex items-center gap-4 text-[11px] text-muted-foreground mb-3">
          <span>{new Date(invoice.date).toLocaleDateString("hr")}</span>
          <span className="text-foreground/10">|</span>
          <span>{invoice.items.length} artikala</span>
          <span className="text-foreground/10">|</span>
          <span>{totalVariations} varijacija</span>
          <span className="text-foreground/10">|</span>
          <span>Σ {totalExpectedQty} kom</span>
        </div>

        {/* Actions */}
        <div className="flex gap-1.5">
          {!isDone && (
            <Button size="sm" variant="outline" onClick={() => setShowAddItem(true)}>
              <Plus className="h-3.5 w-3.5 mr-1.5" />Artikal
            </Button>
          )}
          <Link to="/warehouse/brojanje-robe/$invoiceId/count" params={{ invoiceId }} onClick={handleStartCounting}>
            <Button size="sm">
              <Play className="h-3.5 w-3.5 mr-1.5" />Brojanje
            </Button>
          </Link>
          <a href={`/api/counting/${invoiceId}/pdf`} target="_blank" rel="noopener">
            <Button size="sm" variant="outline">
              <FileDown className="h-3.5 w-3.5 mr-1.5" />PDF
            </Button>
          </a>
          {isDone ? (
            <Button size="sm" variant="outline" onClick={handleReopen} disabled={setStatus.isPending}>
              <Unlock className="h-3.5 w-3.5 mr-1.5" />Ponovo otvori
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={handleFinish} disabled={setStatus.isPending} className="ml-auto text-green-700 dark:text-green-400 hover:text-green-700 dark:hover:text-green-400">
              <CheckCircle className="h-3.5 w-3.5 mr-1.5" />Završi
            </Button>
          )}
        </div>
      </div>

      {/* Items */}
      <div className="flex-1 overflow-y-auto p-4">
        {invoice.items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-sm text-muted-foreground">Nema artikala</p>
            <p className="text-xs text-muted-foreground/60 mt-1">Dodajte artikle ručno ili koristite OCR na listi faktura</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {invoice.items.map((item: any) => {
              const varSum = item.variations.reduce((s: number, v: any) => s + v.expectedQty, 0);
              const mismatch = item.variations.length > 0 && varSum !== item.quantity;

              const grouped = new Map<string, any[]>();
              for (const v of item.variations) {
                const color = parseVariationColor(v.sku, item.vendorCode) || "__flat__";
                if (!grouped.has(color)) grouped.set(color, []);
                grouped.get(color)!.push(v);
              }

              return (
                <div key={item.id} className="ring-1 ring-foreground/[0.06] bg-foreground/[0.01]">
                  {/* Item header */}
                  <div className="flex items-center gap-2 px-3 py-2 border-b border-foreground/[0.04]">
                    <span className="text-xs font-semibold tabular-nums">{item.vendorCode}</span>
                    <span className="text-xs text-muted-foreground truncate flex-1">{item.name}</span>
                    <span className="text-[10px] tabular-nums text-muted-foreground bg-foreground/[0.04] px-1.5 py-0.5">
                      {item.quantity} kom
                    </span>
                    {!isDone && (
                      <div className="flex gap-0.5 ml-1">
                        <Button variant="ghost" size="icon-xs" onClick={() => setEditingItem({ ...item })}>
                          <Edit2 className="h-3 w-3" />
                        </Button>
                        <Button variant="ghost" size="icon-xs" onClick={() => handleDeleteItem(item.id)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Mismatch warning */}
                  {mismatch && (
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/[0.06] border-b border-amber-500/10 text-[11px] text-amber-700 dark:text-amber-400">
                      <AlertTriangle className="h-3 w-3 shrink-0" />
                      <span>Σ varijacija = {varSum}, krov = {item.quantity}</span>
                      <span className="font-semibold ml-auto tabular-nums">
                        {varSum > item.quantity ? "+" : ""}{varSum - item.quantity}
                      </span>
                    </div>
                  )}

                  {/* Variations grouped by color */}
                  <div className="px-3 py-2">
                    {item.variations.length === 0 ? (
                      <p className="text-[11px] text-muted-foreground/60 py-1">Nema varijacija</p>
                    ) : (
                      <div className="flex flex-col gap-2">
                        {Array.from(grouped.entries()).map(([color, vars]) => (
                          <div key={color}>
                            {color !== "__flat__" && (
                              <div className="text-[10px] uppercase tracking-widest text-muted-foreground/60 font-medium mb-1 pl-0.5">
                                {color}
                              </div>
                            )}
                            <div className="flex flex-col">
                              {vars.map((v: any) => (
                                <VariationRow
                                  key={v.id}
                                  variation={v}
                                  invoiceId={invoiceId}
                                  vendorCode={item.vendorCode}
                                  disabled={isDone}
                                  onUpdate={(payload) => updateVariation.mutateAsync(payload)}
                                  onDelete={(payload) => deleteVariation.mutateAsync(payload)}
                                />
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Item actions */}
                  {!isDone && (
                    <div className="flex gap-1.5 px-3 py-2 border-t border-foreground/[0.04]">
                      <Button size="xs" variant="outline" onClick={() => { setShowGenerator(item.id); setGenColors(""); setGenSizes(""); setGenQuantities({}); }}>
                        <Layers className="h-3 w-3 mr-1" />Generiši
                      </Button>
                      <Button size="xs" variant="ghost" onClick={() => { setShowAddVar(item.id); setVarForm({ sku: "", expectedQty: "0" }); }}>
                        <Plus className="h-3 w-3 mr-1" />Pojedinačno
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Dialogs */}
      <Dialog open={showAddItem} onOpenChange={setShowAddItem}>
        <DialogContent>
          <DialogHeader><DialogTitle>Dodaj artikal</DialogTitle></DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label>Šifra</Label>
              <Input value={itemForm.vendorCode} onChange={(e) => setItemForm({ ...itemForm, vendorCode: e.target.value })} placeholder="npr. 127/1" autoFocus />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Naziv</Label>
              <Input value={itemForm.name} onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} placeholder="npr. Majica" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Količina (krov)</Label>
              <Input type="number" value={itemForm.quantity} onChange={(e) => setItemForm({ ...itemForm, quantity: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleAddItem} disabled={!itemForm.vendorCode || !itemForm.name}>Dodaj</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingItem} onOpenChange={(open) => !open && setEditingItem(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Uredi artikal</DialogTitle></DialogHeader>
          {editingItem && (
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <Label>Šifra</Label>
                <Input value={editingItem.vendorCode} onChange={(e) => setEditingItem({ ...editingItem, vendorCode: e.target.value })} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Naziv</Label>
                <Input value={editingItem.name} onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Količina</Label>
                <Input type="number" value={editingItem.quantity} onChange={(e) => setEditingItem({ ...editingItem, quantity: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={handleUpdateItem}>Spremi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!showGenerator} onOpenChange={(open) => { if (!open) { setShowGenerator(null); setGenQuantities({}); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Generiši varijacije</DialogTitle></DialogHeader>
          {(() => {
            const genItem = showGenerator ? invoice.items.find((i: any) => i.id === showGenerator) : null;
            const colorList = genColors.split(",").map((s) => s.trim()).filter(Boolean);
            const sizeList = genSizes.split(",").map((s) => s.trim()).filter(Boolean);
            const showPreview = genItem && colorList.length > 0 && sizeList.length > 0;
            const totalQty = Object.values(genQuantities).reduce((s, v) => s + (Number(v) || 0), 0);
            return (
              <div className="flex flex-col gap-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1.5">
                    <Label>Boje (odvojene zarezom)</Label>
                    <Input value={genColors} onChange={(e) => setGenColors(e.target.value)} placeholder="Crna, Bijela, Plava" autoFocus />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label>Veličine (odvojene zarezom)</Label>
                    <Input value={genSizes} onChange={(e) => setGenSizes(e.target.value)} placeholder="S, M, L, XL" />
                  </div>
                </div>
                {showPreview && (
                  <>
                    <div className="flex items-center justify-between text-[11px] text-muted-foreground tabular-nums bg-foreground/[0.03] px-2.5 py-1.5">
                      <span>{colorList.length} × {sizeList.length} = {colorList.length * sizeList.length} varijacija</span>
                      <span>Σ {totalQty} kom{genItem.quantity ? ` / krov ${genItem.quantity}` : ""}</span>
                    </div>
                    <div className="max-h-[50vh] overflow-y-auto ring-1 ring-foreground/[0.06]">
                      <table className="w-full text-xs">
                        <thead className="bg-foreground/[0.03] sticky top-0">
                          <tr>
                            <th className="text-left font-medium px-2 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">SKU</th>
                            <th className="text-right font-medium px-2 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground w-24">Količina</th>
                          </tr>
                        </thead>
                        <tbody>
                          {colorList.flatMap((color) =>
                            sizeList.map((size) => {
                              const sku = `${genItem.vendorCode}/${color}/${size}`;
                              return (
                                <tr key={sku} className="border-t border-foreground/[0.04]">
                                  <td className="px-2 py-1 tabular-nums">{sku}</td>
                                  <td className="px-1 py-0.5">
                                    <Input
                                      type="number"
                                      inputMode="numeric"
                                      value={genQuantities[sku] ?? ""}
                                      onChange={(e) => setGenQuantities({ ...genQuantities, [sku]: e.target.value })}
                                      className="h-7 text-right tabular-nums"
                                      placeholder="0"
                                    />
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            );
          })()}
          <DialogFooter>
            <Button onClick={handleGenerate}>Generiši</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!showAddVar} onOpenChange={(open) => !open && setShowAddVar(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Dodaj varijaciju</DialogTitle></DialogHeader>
          {(() => {
            const addItem = showAddVar ? invoice.items.find((i: any) => i.id === showAddVar) : null;
            return (
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label>SKU</Label>
                  <div className="flex items-center ring-1 ring-input bg-background h-9 focus-within:ring-2 focus-within:ring-ring">
                    {addItem && (
                      <span className="text-xs text-muted-foreground tabular-nums px-2 select-none whitespace-nowrap">
                        {addItem.vendorCode}/
                      </span>
                    )}
                    <input
                      value={varForm.sku}
                      onChange={(e) => setVarForm({ ...varForm, sku: e.target.value })}
                      placeholder="boja/veličina"
                      autoFocus
                      className="h-full text-sm tabular-nums flex-1 min-w-0 bg-transparent outline-none pr-2"
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>Očekivana količina</Label>
                  <Input type="number" value={varForm.expectedQty} onChange={(e) => setVarForm({ ...varForm, expectedQty: e.target.value })} />
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button onClick={handleAddVariation} disabled={!varForm.sku.trim()}>Dodaj</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
